import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import { sortBy } from "lodash";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import { Button } from "components/haven-design/buttons";
import {
  ApplicantSubmissions,
  ApplicantSubs,
} from "types/gql-types";
import { InterestStage, statusFromStage } from "model/interest/status";
import Application from "./client-application";


const QUERY_APPLICATIONS = gql`
  query ApplicantSubmissions {
    clientInterests {
      id
      status {
        name
        changedAt
      }
      submittedAt
      updateRequest {
        code
        isActive
      }
      interestList {
        id
        name
        isArchived
        property {
          id
          email
          phone
          name
          reinstatementDays
        }
      }
    }
  }
`;
const QUERY_SUBMISSIONS = gql`
  query ApplicantSubs {
    submissions {
      id
      submittedAt
      form {
        type
        property {
          id
          email
          phone
          name
          interestList {
            id
            name
            isArchived
          }
        }
      }
    }
  }
`;

const SkeletonItem: React.FC = () => (
  <Grid size={12}>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <div>
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="text" width="40%" />
        </div>
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <div>
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="text" width="50%" />
        </div>
      </Grid>
      <Grid size={{ xs: 12, md: 3, lg: 4 }}>
        <div>
          <Skeleton variant="rectangular" height={44} />
          <Skeleton variant="rectangular" height={44} style={{ marginTop: 12 }} />
        </div>
      </Grid>
    </Grid>
    <hr />
  </Grid>
);

type ClientApplicationsParams = {setInterestsLength: React.Dispatch<React.SetStateAction<number>>}

const ClientApplications: React.FC<ClientApplicationsParams> = ({ setInterestsLength }) => {
  const result = useQuery<ApplicantSubmissions>(QUERY_APPLICATIONS, { fetchPolicy: "no-cache" });
  useEffect(() => {
    if (result.data?.clientInterests) {
      setInterestsLength(result.data.clientInterests.length);
    }
  }, [result]);
  if (result.loading) return <SkeletonItem />;
  if (result.error || !result.data) return null;
  const { clientInterests } = result.data;

  return <>{sortBy(
    clientInterests,
    (interest) => statusFromStage(interest.status.name as InterestStage),
    (interest) => interest.submittedAt,
  ).map((application) => (
    <Application
      status={statusFromStage(application.status.name)}
      statusChangedAt={application.status.changedAt ?? ""}
      key={application.id}
      data={application}
      onApplicationRemoved={() => result.refetch()}
      interestList={application.interestList}
      property={application.interestList.property}
      submittedAt={application.submittedAt}
      type="application"
    />
  ))}</>;
};

type UserSubmissionsParams = {setSubmissionsLength: React.Dispatch<React.SetStateAction<number>>}
const UserSubmissions: React.FC<UserSubmissionsParams> = ({ setSubmissionsLength }) => {
  const result = useQuery<ApplicantSubs>(QUERY_SUBMISSIONS, { fetchPolicy: "no-cache" });

  useEffect(() => {
    if (result.data?.submissions) {
      setSubmissionsLength(result.data.submissions.length);
    }
  }, [result]);
  if (result.loading) return <SkeletonItem />;
  if (result.error || !result.data) return null;
  let { data: { submissions } } = result;

  // Only incomplete submissions are shown:
  submissions = submissions.filter(({ submittedAt }) => submittedAt === null);
  return <>
    {submissions.map((submission) => (
      <Application
        status="Draft"
        statusChangedAt=""
        key={submission.id}
        type="submission"
        property={{ ...submission.form.property, reinstatementDays: 0 }}
        interestList={submission.form.property.interestList}
        data={submission}
        submittedAt={submission.submittedAt ? Number(submission.submittedAt) : null}
        onApplicationRemoved={() => result.refetch()}
      />))}</>;
};

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const [submissionsLength, setSubmissionsLength] = useState(-1);
  const [interestsLength, setInterestsLength] = useState(-1);
  return (
    <div style={{ margin: 16 }}>
      <Grid container spacing={2}>
        <UserSubmissions setSubmissionsLength={setSubmissionsLength} />
        <ClientApplications setInterestsLength={setInterestsLength}/>
        {submissionsLength === 0 && interestsLength === 0 && (
          <div style={{ textAlign: "center" }}>
            <div>No applications submitted.</div>
            <div style={{ marginTop: 18 }}>
              <Button
                id="find-housing-button"
                size="L"
                color="primary"
                onClick={() => navigate("/properties")}
              >
                Find Housing
              </Button>
            </div>
          </div>
        )}
      </Grid>
    </div>
  );
};

export default Applications;
